import moment from 'moment';
import { D as DateUnit } from './unit-e9fb035f.js';

var DateComparePrecision;
(function (DateComparePrecision) {
    DateComparePrecision["Year"] = "year";
    DateComparePrecision["Month"] = "month";
    DateComparePrecision["Day"] = "day";
    DateComparePrecision["Minutes"] = "minutes";
    DateComparePrecision["Seconds"] = "seconds";
    DateComparePrecision["Milliseconds"] = "milliseconds";
})(DateComparePrecision || (DateComparePrecision = {}));
var LogicalConditions;
(function (LogicalConditions) {
    LogicalConditions["NotEqual"] = "!==";
    LogicalConditions["Equal"] = "===";
    LogicalConditions["Greater"] = ">";
    LogicalConditions["GreaterOrEqual"] = ">=";
    LogicalConditions["Lower"] = "<";
    LogicalConditions["LowerOrEqual"] = "<=";
})(LogicalConditions || (LogicalConditions = {}));

/**
 * @deprecated Use `ComparisonDateUtils` instead (moment.js is not supported anymore)
 */
class ComparisonDateUtilsDeprecated {
    static compareDates(date1, condition, date2, comparePrecision, dateTimeFormat) {
        const formattedDate1 = moment(date1, dateTimeFormat);
        const formattedDate2 = moment(date2, dateTimeFormat);
        const conditions = {
            [LogicalConditions.Equal]: formattedDate1.isSame(formattedDate2, comparePrecision),
            [LogicalConditions.NotEqual]: !formattedDate1.isSame(formattedDate2, comparePrecision),
            [LogicalConditions.Greater]: formattedDate1.isAfter(formattedDate2, comparePrecision),
            [LogicalConditions.GreaterOrEqual]: formattedDate1.isSameOrAfter(formattedDate2, comparePrecision),
            [LogicalConditions.Lower]: formattedDate1.isBefore(formattedDate2, comparePrecision),
            [LogicalConditions.LowerOrEqual]: formattedDate1.isSameOrBefore(formattedDate2, comparePrecision),
        };
        return conditions[condition];
    }
    static diff(value1, value2, unit = DateUnit.Seconds) {
        return moment(value1).diff(value2, unit);
    }
    static isBetween(value, from, to, granularity = 'days', inclusivity = '[]') {
        return moment(value).isBetween(from, to, granularity, inclusivity);
    }
    static isBefore(date1, date2) {
        return moment(date1).isBefore(date2);
    }
    static isSameOrBefore(date1, date2) {
        return moment(date1).isSameOrBefore(date2);
    }
    static isAfter(date1, date2) {
        return moment(date1).isAfter(date2);
    }
    static isSameOrAfter(date1, date2) {
        return moment(date1).isSameOrAfter(date2);
    }
    static isColliding(range1, range2, options) {
        var _a;
        const exact = (_a = options === null || options === void 0 ? void 0 : options.exact) !== null && _a !== void 0 ? _a : true;
        const isBefore = exact
            ? ComparisonDateUtilsDeprecated.isSameOrBefore
            : ComparisonDateUtilsDeprecated.isBefore;
        const isAfter = exact
            ? ComparisonDateUtilsDeprecated.isSameOrAfter
            : ComparisonDateUtilsDeprecated.isAfter;
        return (isAfter(range2.end, range1.start) && isBefore(range2.start, range1.end));
    }
    static isOutOfRange(range1, range2, options) {
        var _a;
        const exact = (_a = options === null || options === void 0 ? void 0 : options.exact) !== null && _a !== void 0 ? _a : false;
        const isBefore = exact
            ? ComparisonDateUtilsDeprecated.isSameOrBefore
            : ComparisonDateUtilsDeprecated.isBefore;
        const isAfter = exact
            ? ComparisonDateUtilsDeprecated.isSameOrAfter
            : ComparisonDateUtilsDeprecated.isAfter;
        const isOutOfStartRange = isBefore(range1.start, range2.start) &&
            isBefore(range1.end, range2.start);
        const isOutOfEndRange = isAfter(range1.start, range2.end) && isAfter(range1.end, range2.end);
        return isOutOfStartRange || isOutOfEndRange;
    }
    static isSameDay(date1, date2) {
        return ComparisonDateUtilsDeprecated.compareDates(date1, LogicalConditions.Equal, date2, DateComparePrecision.Day);
    }
}

const API_MATCH_FULL_BIRTHDAY_FORMAT = 'DD-MM-YYYY';
const API_MATCH_PARTIAL_BIRTHDAY_FORMAT = 'DD-MM';
const API_DATE_FORMAT = 'YYYY-MM-DD';
const API_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const API_DATE_TIME_UTC_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
const API_TIME_FORMAT = 'HH:mm:ss';
const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY';
const DISPLAY_TIME_FORMAT = 'HH:mm';
const DISPLAY_TIME_FORMAT_12_HOURS = 'hh:mm A';
var DurationFormat;
(function (DurationFormat) {
    DurationFormat["HoursMinutesSeconds"] = "HH:mm:ss";
    DurationFormat["HoursMinutes"] = "HH:mm";
    DurationFormat["Hours"] = "HH";
    DurationFormat["Minutes"] = "mm";
})(DurationFormat || (DurationFormat = {}));

/**
 * @deprecated Use `SerializationDateUtils` instead (moment.js is not supported anymore)
 */
class SerializationDateUtilsDeprecated {
    static getMomentFromServerString(value) {
        return moment(value, API_DATE_FORMAT);
    }
    static getMomentWithTimeFromServerString(value) {
        return moment(value, API_DATE_TIME_FORMAT);
    }
    static getDateFromServerString(value) {
        return SerializationDateUtilsDeprecated.getMomentFromServerString(value).toDate();
    }
    static getDateTimeFromServerString(value) {
        return SerializationDateUtilsDeprecated.getMomentWithTimeFromServerString(value).toDate();
    }
    static formatDateForApi(date) {
        return date ? moment(date).format(API_DATE_FORMAT) : '';
    }
    static formatTimeForApi(date) {
        return date ? moment(date).format(API_TIME_FORMAT) : '';
    }
    static formatDateTimeForApi(date) {
        return date ? moment(date).format(API_DATE_TIME_FORMAT) : '';
    }
    static formatDateTimeForDisplay(dateTime, inputFormat, outputFormat) {
        const resolvedInputFormat = inputFormat || [
            API_DATE_FORMAT,
            API_TIME_FORMAT,
            API_DATE_TIME_FORMAT,
        ];
        const resolvedOutputFormat = outputFormat || `${DISPLAY_DATE_FORMAT} ${DISPLAY_TIME_FORMAT}`;
        return moment(dateTime, resolvedInputFormat).format(resolvedOutputFormat);
    }
    static formatTimeTo24(timeString) {
        return moment(timeString, [DISPLAY_TIME_FORMAT_12_HOURS]).format(DISPLAY_TIME_FORMAT);
    }
    static formatTimeTo12(timeString) {
        return moment(timeString, [DISPLAY_TIME_FORMAT]).format(DISPLAY_TIME_FORMAT_12_HOURS);
    }
    static format(date, format) {
        return date ? moment(date).format(format) : '';
    }
}

/**
 * @deprecated Use `PeriodDateUtils` instead (moment.js is not supported anymore)
 */
class PeriodDateUtilsDeprecated {
    static getDayStringArrayFromPeriod(from, to, missLastDay = false) {
        const firstItemDay = moment(from).add(-1, 'days');
        return Array(this.getDayCount(from, to) + (missLastDay ? 0 : 1))
            .fill(null)
            .map(() => firstItemDay.add(1, 'days').format(API_DATE_FORMAT));
    }
    static getDayStringArrayFromDatePeriod(from, to, missLastDay = false) {
        const firstItemDay = moment(from).add(-1, 'days');
        return Array(this.getDayCount(from, to) + (missLastDay ? 0 : 1))
            .fill(null)
            .map(() => firstItemDay.add(1, 'days').format(API_DATE_FORMAT));
    }
    static getDayCount(from, to, stringDateFormat) {
        return moment(to, stringDateFormat)
            .startOf('day')
            .diff(moment(from, stringDateFormat).startOf('day'), 'days');
    }
}

/**
 * @deprecated Use `DateUtils` instead (moment.js is not supported anymore)
 */
class DateUtilsDeprecated {
}
DateUtilsDeprecated.comparison = ComparisonDateUtilsDeprecated;
DateUtilsDeprecated.period = PeriodDateUtilsDeprecated;
DateUtilsDeprecated.serialization = SerializationDateUtilsDeprecated;

/**
 * @deprecated Use `DurationDateUtils` instead (moment.js is not supported anymore)
 */
class DurationDateUtilsDeprecated {
    static partials(time, unit) {
        const duration = moment.duration(Math.abs(time), unit);
        const hours = Math.floor(duration.asHours());
        const minutes = Math.floor(duration.asMinutes()) - hours * 60;
        return {
            hours,
            minutes,
            seconds: Math.floor(duration.asSeconds()) - minutes * 60 - hours * 60 * 60,
        };
    }
    static format(partials, format) {
        const hours = partials.hours.toString().padStart(2, '0');
        const minutes = partials.minutes.toString().padStart(2, '0');
        const seconds = partials.seconds.toString().padStart(2, '0');
        const formats = {
            [DurationFormat.HoursMinutesSeconds]: `${hours}:${minutes}:${seconds}`,
            [DurationFormat.HoursMinutes]: `${hours}:${minutes}`,
            [DurationFormat.Hours]: hours,
            [DurationFormat.Minutes]: minutes,
        };
        if (!formats[format]) {
            throw new Error('Unsupported duration format provided!');
        }
        return formats[format];
    }
}

export { API_DATE_FORMAT as A, ComparisonDateUtilsDeprecated as C, DurationDateUtilsDeprecated as D, LogicalConditions as L, PeriodDateUtilsDeprecated as P, SerializationDateUtilsDeprecated as S, API_DATE_TIME_UTC_FORMAT as a, DateUtilsDeprecated as b, DateComparePrecision as c, API_MATCH_FULL_BIRTHDAY_FORMAT as d, API_MATCH_PARTIAL_BIRTHDAY_FORMAT as e, API_DATE_TIME_FORMAT as f, API_TIME_FORMAT as g, DISPLAY_DATE_FORMAT as h, DISPLAY_TIME_FORMAT as i, DISPLAY_TIME_FORMAT_12_HOURS as j, DurationFormat as k };
//# sourceMappingURL=duration-1387802c.js.map
